"use client";

import { Controller, useWatch } from "react-hook-form";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Box,
  TextField,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";

export default function Step3Team({ control, setValue, prev, next }) {
  const schools = [
    "The Lawrence School Sanawar",
    "Bhavan Vidyalaya",
    "The Doon School, Dehradun",
    "The Lawrence School, Lovedale, Ooty",
    "Sherwood College, Nainital",
    "The Scindia School, Gwalior",
    "Daly College, Indore",
    "Emerald Heights, Indore",
    "P.P.S. Nabha",
    "Y.P.S. Patiala",
    "Mayo College, Ajmer",
    "Rishi Valley School, Chittoor",
    "Bishop cotton school Shimla",
    "Woodstock, Mussorrie",
    "Kodai International",
    "St. Edward's School",
    "Auckland House School for Boys",
    "Trinity International School",
    "Stokes Memorial School",
    "YPS Mohali",
    "Other",
  ];

  const raceCategories = [
    "School Juniors - Boys (born on or after 1st Jan 2012)",
    "School Junior - Girls (born on or after 1st Jan 2012)",
    "School Senior - Boys (born on or after 1st Jan 2007)",
    "School Senior - Girls (born on or after 1st Jan 2007)",
  ];

  // Watch fields
  const officialSchoolTeam = useWatch({ control, name: "officialSchoolTeam" });
  const schoolRaceCategory = useWatch({ control, name: "raceCategory" });

  // Local state for manual school name
  const [manualSchoolName, setManualSchoolName] = useState("");
  const otherInputRef = useRef(null);

  // Auto-focus when "Other" selected
  useEffect(() => {
    if (officialSchoolTeam === "Other" && otherInputRef.current) {
      otherInputRef.current.focus();
    }
  }, [officialSchoolTeam]);

  // Enable Next button only if valid
  const isNextEnabled =
    !!schoolRaceCategory &&
    ((officialSchoolTeam && officialSchoolTeam !== "Other") ||
      (officialSchoolTeam === "Other" && manualSchoolName.trim().length > 0));

  // Handle Next click
  const handleNext = () => {
    if (officialSchoolTeam === "Other" && manualSchoolName.trim()) {
      // overwrite "Other" with typed school
      setValue("officialSchoolTeam", manualSchoolName.trim(), {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
    next();
  };

  return (
    <div
      style={{
        maxWidth: "650px",
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
      }}
      className="rounded-lg shadow bg-white-1"
    >
      <span className="block py-1 text-center text-base rounded-t-lg font-bold bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white-1">
        6th Tour De Sanawar 2025
      </span>

      <div className="w-full h-full p-6 pt-0">
        <div style={{ textAlign: "center" }}>
          <h2
            style={{
              fontSize: "1.875rem",
              fontWeight: "800",
              color: "#1f2937",
            }}
          >
            Team Participant Details
          </h2>
          <p
            style={{
              marginTop: "0.5rem",
              color: "#6b7280",
              fontSize: "0.875rem",
            }}
          >
            For participants from registered school teams
          </p>
        </div>

        <div className="mt-5">
          {/* School Team */}
          <FormControl fullWidth style={{ marginBottom: "1rem" }}>
            <InputLabel>Select your official school team</InputLabel>
            <Controller
              name="officialSchoolTeam"
              control={control}
              render={({ field }) => (
                <Select {...field} label="Select your official school team">
                  {schools.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />

            {/* Manual input when 'Other' selected */}
            {officialSchoolTeam === "Other" && (
              <TextField
                fullWidth
                inputRef={otherInputRef}
                value={manualSchoolName}
                onChange={(e) => setManualSchoolName(e.target.value)}
                label="Enter your school name"
                placeholder="Type your school name"
                sx={{ mt: 2 }}
              />
            )}

            {/* Important Note */}
            <Box
              sx={{
                width: "100%",
                backgroundColor: "#f9f9f9",
                border: "1px solid #ddd",
                borderRadius: "4px",
                padding: "16px",
                mt: 2,
              }}
            >
              <Typography
                variant="body1"
                sx={{ fontWeight: "bold", color: "#333", mb: 1 }}
              >
                Important Note:
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, lineHeight: 1.6 }}
              >
                • A minimum of <strong>5 students</strong> from a respective
                school are required to be eligible for{" "}
                <strong>school category participation</strong>.
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, lineHeight: 1.6 }}
              >
                • The school has to send an official confirmation email at{" "}
                <strong>sanawarglobalcycling@gmail.com</strong>.
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, lineHeight: 1.6 }}
              >
                • If participation is less than 5 students, the participant will
                have to compete in the <strong>open amateur category</strong>.
              </Typography>
            </Box>
          </FormControl>

          {/* Race Category */}
          <FormControl fullWidth>
            <InputLabel>Select your race category</InputLabel>
            <Controller
              name="raceCategory"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  label="Select your race category"
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 300,
                        "& .MuiMenuItem-root": {
                          whiteSpace: "normal",
                        },
                      },
                    },
                  }}
                >
                  {raceCategories.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </FormControl>
        </div>

        {/* Navigation Buttons */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "1.5rem",
            gap: 20,
          }}
        >
          <Button
            variant="outlined"
            onClick={prev}
            sx={{
              borderColor: "var(--color-blue)",
              color: "var(--color-blue)",
              "&:hover": {
                borderColor: "var(--color-blue-hover)",
                color: "var(--color-blue-hover)",
              },
            }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            sx={{
              backgroundColor: "var(--color-blue)",
              "&:hover": { backgroundColor: "var(--color-blue-hover)" },
            }}
            onClick={handleNext}
            disabled={!isNextEnabled}
          >
            Next (Payment)
          </Button>
        </div>
      </div>
    </div>
  );
}
